import React, { createContext, FC, ReactNode, useCallback, useMemo, useState } from 'react';

import { config, moment } from 'data';
import { lang } from 'helpers';
import { Locale } from 'types/locales';

type ContextValue = {
  locale: Locale;
  has: typeof lang.has;
  get: typeof lang.get;
  choice: typeof lang.choice;
  changeLocale: typeof lang.changeLocale;
};

type ProviderProps = {
  children: ReactNode;
};

const LangContext = createContext<ContextValue>({
  locale: config.DEFAULT_LOCALE as Locale,
  has: lang.has,
  get: lang.get,
  choice: lang.choice,
  changeLocale: lang.changeLocale,
});

const LangProvider: FC<ProviderProps> = ({ children }) => {
  const [locale, setLocale] = useState<Locale>(lang.getLocale());

  const changeLocale = useCallback(async (locale: Locale) => {
    await lang.changeLocale(locale);

    setLocale(lang.getLocale());
  }, []);

  moment.locale(locale);

  const value = useMemo<ContextValue>(() => ({
    locale,
    has: lang.has,
    get: lang.get,
    choice: lang.choice,
    changeLocale,
  }), [locale, changeLocale]);

  return (
    <LangContext.Provider value={value}>
      {children}
    </LangContext.Provider>
  );
};

const Lang = {
  Context: LangContext,
  Provider: LangProvider,
};

export default Lang;
